import { apiService } from './api';
import { Tenant, LLMConfig, NotificationConfig, MonitoringConfig, ImageConfig, ApiResponse } from '../types';

class TenantApi {
  async listTenants(includeDeleted = false): Promise<Tenant[]> {
    const params = includeDeleted ? '?includeDeleted=true' : '';
    const response = await apiService.get<ApiResponse<Tenant[]>>(`/v1/root/tenants${params}`);
    return response.data || [];
  }

  async getTenant(id: string): Promise<Tenant> {
    const response = await apiService.get<ApiResponse<Tenant>>(`/v1/root/tenants/${id}`);
    if (!response.data) {
      throw new Error('Tenant not found');
    }
    return response.data;
  }

  async createTenant(tenant: Partial<Tenant>): Promise<Tenant> {
    const response = await apiService.post<ApiResponse<Tenant>>('/v1/root/tenants', tenant);
    if (!response.data) {
      throw new Error('Failed to create tenant');
    }
    return response.data;
  }

  async updateTenant(id: string, tenant: Partial<Tenant>): Promise<Tenant> {
    const response = await apiService.put<ApiResponse<Tenant>>(`/v1/root/tenants/${id}`, tenant);
    if (!response.data) {
      throw new Error('Failed to update tenant');
    }
    return response.data;
  }

  async deleteTenant(id: string): Promise<void> {
    await apiService.delete(`/v1/root/tenants/${id}`);
  }

  async getLLMSettings(tenantId: string): Promise<LLMConfig> {
    const response = await apiService.get<ApiResponse<LLMConfig>>(
      `/v1/root/tenants/${tenantId}/settings/llm`
    );
    if (!response.data) {
      // Return default config if not set
      return {
        model: 'gpt-5-mini',
        reasoningEffort: 'minimal',
        verbosity: 'low',
        maxCompletionTokens: 2048,
      };
    }
    return response.data;
  }

  async updateLLMSettings(tenantId: string, config: LLMConfig): Promise<LLMConfig> {
    const response = await apiService.put<ApiResponse<LLMConfig>>(
      `/v1/root/tenants/${tenantId}/settings/llm`,
      config
    );
    if (!response.data) {
      throw new Error('Failed to update LLM settings');
    }
    return response.data;
  }

  // Current tenant methods (for non-root admins)
  async getCurrentTenant(): Promise<Tenant> {
    const response = await apiService.get<ApiResponse<Tenant>>('/v1/admin/tenant');
    if (!response.data) {
      throw new Error('Failed to get current tenant');
    }
    return response.data;
  }

  async updateCurrentTenant(tenant: Partial<Tenant>): Promise<Tenant> {
    const response = await apiService.put<ApiResponse<Tenant>>('/v1/admin/tenant', tenant);
    if (!response.data) {
      throw new Error('Failed to update tenant');
    }
    return response.data;
  }

  async updateAPIKeys(apiKeys: { openai?: string; lineChannelAccessToken?: string }): Promise<void> {
    await apiService.put('/v1/admin/tenant/api-keys', apiKeys);
  }

  async updateMessageTemplates(templates: { calling: string; waiting: string }): Promise<void> {
    await apiService.put('/v1/admin/tenant/message-templates', templates);
  }

  async getNotificationSettings(tenantId: string): Promise<NotificationConfig> {
    const response = await apiService.get<ApiResponse<NotificationConfig>>(
      `/v1/root/tenants/${tenantId}/settings/notification`
    );
    if (!response.data) {
      // Return default config if not set
      return {
        callingField: 'calling',
        waitingField: 'waiting',
        roomField: 'room',
        nameField: 'name',
        notifyOnRegistration: true,
        notifyOnCalling: true,
        notifyOnWaiting: false,
      };
    }
    return response.data;
  }

  async updateNotificationSettings(tenantId: string, config: NotificationConfig): Promise<NotificationConfig> {
    const response = await apiService.put<ApiResponse<NotificationConfig>>(
      `/v1/root/tenants/${tenantId}/settings/notification`,
      config
    );
    if (!response.data) {
      throw new Error('Failed to update notification settings');
    }
    return response.data;
  }

  async getMonitoringSettings(tenantId: string): Promise<MonitoringConfig> {
    const response = await apiService.get<ApiResponse<MonitoringConfig>>(
      `/v1/root/tenants/${tenantId}/settings/monitoring`
    );
    if (!response.data) {
      // Return default config if not set
      return {
        screenUploadMonitoring: {
          enabled: false,
          slackWebhookUrl: '',
          noUploadCheck: false,
          thresholdMinutes: 5,
          monitoredScreenCount: 1,
        },
      };
    }
    return response.data;
  }

  async updateMonitoringSettings(tenantId: string, config: MonitoringConfig): Promise<MonitoringConfig> {
    const response = await apiService.put<ApiResponse<MonitoringConfig>>(
      `/v1/root/tenants/${tenantId}/settings/monitoring`,
      config
    );
    if (!response.data) {
      throw new Error('Failed to update monitoring settings');
    }
    return response.data;
  }

  async getCurrentImageSettings(): Promise<ImageConfig> {
    const response = await apiService.get<ApiResponse<ImageConfig>>(
      '/v1/admin/tenant/settings/image'
    );
    return response.data ?? { enableCrop: false };
  }

  async updateCurrentImageSettings(config: ImageConfig): Promise<ImageConfig> {
    const response = await apiService.put<ApiResponse<ImageConfig>>(
      '/v1/admin/tenant/settings/image',
      config
    );
    if (!response.data) {
      throw new Error('Failed to update image settings');
    }
    return response.data;
  }

  async getImageSettings(tenantId: string): Promise<ImageConfig> {
    const response = await apiService.get<ApiResponse<ImageConfig>>(
      `/v1/root/tenants/${tenantId}/settings/image`
    );
    return response.data ?? { enableCrop: false };
  }

  async updateImageSettings(tenantId: string, config: ImageConfig): Promise<ImageConfig> {
    const response = await apiService.put<ApiResponse<ImageConfig>>(
      `/v1/root/tenants/${tenantId}/settings/image`,
      config
    );
    if (!response.data) {
      throw new Error('Failed to update image settings');
    }
    return response.data;
  }
}

export const tenantApi = new TenantApi();